import React, { ReactNode, useState } from 'react';
import { Box, AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItemIcon, ListItemText, ListItemButton, Collapse } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Users, Store, Code, LogOut, Bot, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: ReactNode;
}

const DRAWER_WIDTH = 260;

interface MenuItem {
  label: string;
  path: string;
  icon: any;
}

interface CourseGroup {
  label: string;
  items: MenuItem[];
}

const menuGroups: (MenuItem | CourseGroup)[] = [
  { label: 'Dashboard', path: '/', icon: Home },
  { label: 'Users', path: '/users', icon: Users },
  {
    label: 'UAUP',
    items: [
      { label: 'Organizations', path: '/uaup/organizations', icon: Store },
      { label: 'Users', path: '/uaup/users', icon: Users },
      { label: 'Projects', path: '/uaup/projects', icon: Code },
    ],
  },
  {
    label: 'Intro to AI',
    items: [
      { label: 'Organizations', path: '/intro-to-ai/organizations', icon: Store },
      { label: 'Users', path: '/intro-to-ai/users', icon: Users },
    ],
  },
  {
    label: 'GenAI',
    items: [
      { label: 'Users', path: '/genai/users', icon: Users },
    ],
  },
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logout } = useAuth();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    'UAUP': true,
    'Intro to AI': true,
    'GenAI': true,
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupLabel]: !prev[groupLabel],
    }));
  };

  const isGroupItem = (item: MenuItem | CourseGroup): item is CourseGroup => {
    return 'items' in item;
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${DRAWER_WIDTH}px)`,
          ml: `${DRAWER_WIDTH}px`,
          bgcolor: 'white',
          color: '#142060',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            {admin?.clientName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            {admin?.email}
          </Typography>
          <IconButton onClick={handleLogout} color="inherit">
            <LogOut size={20} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: '#f8f9fa',
            borderRight: '1px solid #e0e0e0',
          },
        }}
      >
        <Toolbar sx={{ justifyContent: 'center', py: 3 }}>
          <img
            src="/assets/logoBlack.svg"
            alt="TechTeach Logo"
            style={{ height: '40px', objectFit: 'contain' }}
          />
        </Toolbar>

        <List sx={{ px: 2 }}>
          {menuGroups.map((item) => {
            if (isGroupItem(item)) {
              const isExpanded = expandedGroups[item.label];

              return (
                <Box key={item.label}>
                  <ListItemButton
                    onClick={() => toggleGroup(item.label)}
                    sx={{
                      mb: 0.5,
                      borderRadius: 2,
                      color: '#142060',
                      '&:hover': {
                        bgcolor: 'rgba(99, 85, 255, 0.08)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    />
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </ListItemButton>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.items.map((subItem) => {
                        const Icon = subItem.icon;
                        const isActive = location.pathname === subItem.path;

                        return (
                          <ListItemButton
                            key={subItem.path}
                            onClick={() => navigate(subItem.path)}
                            sx={{
                              pl: 4,
                              mb: 0.5,
                              borderRadius: 2,
                              bgcolor: isActive ? '#6355FF' : 'transparent',
                              color: isActive ? 'white' : '#142060',
                              '&:hover': {
                                bgcolor: isActive ? '#6355FF' : 'rgba(99, 85, 255, 0.08)',
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                              <Icon size={20} />
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.label}
                              primaryTypographyProps={{
                                fontSize: 14,
                                fontWeight: isActive ? 600 : 500,
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              );
            } else {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <ListItemButton
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    mb: 0.5,
                    borderRadius: 2,
                    bgcolor: isActive ? '#6355FF' : 'transparent',
                    color: isActive ? 'white' : '#142060',
                    '&:hover': {
                      bgcolor: isActive ? '#6355FF' : 'rgba(99, 85, 255, 0.08)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                    <Icon size={20} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              );
            }
          })}
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          bgcolor: '#fafafa',
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
